import { useEffect, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import SubstitutePicker from './SubstitutePicker';
import CoverageWarningList from './CoverageWarningList';
import { useCoverageOptions, useSaveSubstitutes } from '../../hooks/useTimeOffCoverage';
import type { SubstituteSelection } from '../../services/api/timeOffCoverage';

/**
 * MKS-361 — the PM's "set or change the substitute".
 *
 * Since the 2026-10-19 follow-up this is the ONLY place coverage is written:
 * the requester's form offers no picker. Reachable from the approval queue
 * both before and after the request is approved — Petra's item 6, "the PM can
 * still change it afterwards".
 *
 * Only the projects the server marked `canEdit` are sent back. The PUT is a
 * replace within the actor's scope, so including a read-only project would
 * either be refused (403 naming it) or, worse if the rule ever loosened,
 * rewrite a pick another PM made.
 */

interface EditSubstitutesModalProps {
  timeOffId: number;
  requesterName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditSubstitutesModal({
  timeOffId,
  requesterName,
  isOpen,
  onClose,
}: EditSubstitutesModalProps) {
  const coverageQuery = useCoverageOptions({ timeOffId }, isOpen);
  const saveMutation = useSaveSubstitutes();
  const [substitutes, setSubstitutes] = useState<SubstituteSelection[]>([]);
  const [error, setError] = useState<string | null>(null);

  const projects = coverageQuery.data?.projects ?? [];
  const editableProjectIds = new Set(
    projects.filter((project) => project.canEdit).map((project) => project.projectId),
  );
  const mayEditAnything = editableProjectIds.size > 0;

  // Adopt the stored picks whenever the server's answer arrives (and again if
  // it changes under us because somebody else edited the request).
  useEffect(() => {
    setSubstitutes(
      projects.flatMap((project) =>
        project.selected
          ? [
              {
                projectId: project.projectId,
                substituteUserId: project.selected.substituteUserId,
                note: project.selected.note,
              },
            ]
          : [],
      ),
    );
  }, [coverageQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setError(null);
    try {
      await saveMutation.mutateAsync({
        timeOffId,
        substitutes: substitutes.filter((s) => editableProjectIds.has(s.projectId)),
      });
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to save the substitutes.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Coverage for ${requesterName}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {mayEditAnything ? 'Cancel' : 'Close'}
          </Button>
          {mayEditAnything && (
            <Button onClick={handleSave} isLoading={saveMutation.isPending}>
              Save
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {coverageQuery.isLoading ? (
          <Spinner />
        ) : projects.length === 0 ? (
          <p className="text-sm text-gray-500">
            This person is on no project over these dates, so there is nothing to cover.
          </p>
        ) : !mayEditAnything ? (
          <>
            <p className="text-sm text-gray-500">
              You manage none of these projects, so this is read-only.
            </p>
            <SubstitutePicker projects={projects} value={substitutes} onChange={setSubstitutes} />
          </>
        ) : (
          <SubstitutePicker
            projects={projects}
            value={substitutes}
            onChange={setSubstitutes}
            disabled={saveMutation.isPending}
          />
        )}

        <CoverageWarningList
          warnings={coverageQuery.data?.warnings ?? []}
          audience="approver"
          requesterName={requesterName}
        />
      </div>
    </Modal>
  );
}
